import { apiClient } from '@/shared/api/client'
import type { CallerIdStrategy } from '@/shared/api/queue'

const BASE = '/api/pexitalk/telephony'

export interface AsteriskStatus {
  available: boolean
  version: string
  agents_online: number
  endpoints_total: number
  active_channels: number
  /** RDV pris aujourd'hui (tenant, tous statuts) — KPIs de supervision. */
  appointments_today?: number
  /** Parmi eux, ceux au statut « validé » (les seuls qui comptent). */
  appointments_today_validated?: number
  system?: any
}

export interface LiveChannel {
  id: string
  name: string
  state: string
  caller_number: string
  caller_name: string
  connected_number: string
  creationtime: string
}

export interface Trunk {
  id: number
  name: string
  host: string
  username: string
  port: number
  transport: 'udp' | 'tcp' | 'tls'
  /** Format présenté à l'opérateur : 'e164' (+E.164, défaut) ou 'national'. */
  dial_format: 'e164' | 'national'
  /** Indicatif pays sans + (ex: '1', '33') pour compléter en E.164. */
  country_code: string
  /** Le trunk accepte plusieurs appels sortants simultanés (requis pour les
   *  modes Power / Prédictif du dialer). Mono-canal par défaut. */
  multichannel: boolean
  /** Nombre d'appels sortants simultanés supportés (si multicanal). */
  max_channels: number
  enabled: boolean
  is_registered: boolean
  /** État détaillé de la dernière vérification de registration (cause d'échec). */
  registration_status: 'unknown' | 'registered' | 'rejected' | 'no_response' | 'unverified'
  /** Libellé lisible du statut (ex: « Identifiant ou mot de passe refusé »). */
  registration_label: string
  /** Conseil concret affiché en cas d'échec (vide si enregistré). */
  registration_hint: string
  registration_checked_at: string | null
  did_count: number
  created_at: string
  /** Stratégie de rotation du pool de numéros présentés (`caller_id_pool`). */
  caller_id_strategy: CallerIdStrategy
  /** Santé de la ligne : 'down' = en panne (registration refusée ou appels
   *  rejetés par l'opérateur) → les agents autorisés basculent sur le SIP par
   *  défaut (ligne de secours) le temps que la ligne revienne. */
  health_status: 'healthy' | 'down'
  /** Libellé lisible de la santé (ex. « En panne »). */
  health_label: string
  last_failure_at: string | null
  /** Cause de la dernière panne détectée (vide si aucune). */
  last_failure_cause: string
  /** Fin de la fenêtre de bascule (ISO) — la ligne est réessayée ensuite. */
  down_until: string | null
  /** Pool de numéros présentés du trunk (lecture seule ; édition via
   *  `trunk-caller-ids/`, depuis Réglages sous le SIP par défaut). Facultatif :
   *  vide, le routage présente `default_caller_id` en repli. */
  caller_id_pool: TrunkCallerId[]
}

/** Numéro présenté appartenant au pool d'un trunk (rotation par trunk). */
export interface TrunkCallerId {
  id: number
  trunk: number
  number: string
  label: string
  is_active: boolean
  order: number
  max_per_day: number
}

export interface DID {
  id: number
  number: string
  label: string
  trunk: number | null
  trunk_name: string | null
  ring_group: number | null
  ring_group_name: string | null
  enabled: boolean
  transcription_enabled: boolean
  route_count: number
}

export interface InboundRoute {
  id: number
  name: string
  did: number
  did_number: string
  destination_type: 'extension' | 'queue' | 'ivr' | 'voicemail'
  destination_value: string
  timeout_seconds: number
  fallback_destination: string
}

export type RingStrategy =
  | 'ring_all' | 'round_robin' | 'longest_idle' | 'fewest_calls' | 'random'

export interface RingGroup {
  id: number
  name: string
  team: number | null
  team_name: string | null
  members: number[]
  members_detail: Array<{ id: number; username: string }>
  dids: Array<{ id: number; number: string }>
  strategy: RingStrategy
  strategy_label: string
  ring_timeout_seconds: number
  max_wait_seconds: number
  wrap_up_seconds: number
  fallback_destination: string
  enabled: boolean
  last_agent_id: number | null
  created_at: string
  updated_at: string
}

export interface TelephonySettings {
  /** SIP par défaut = dernier maillon de la cascade ET ligne de secours. */
  default_trunk: number | null
  default_trunk_name: string | null
  default_caller_id: string
  /** Nombre de numéros actifs dans le pool du SIP par défaut (lecture seule). */
  default_trunk_pool_size?: number
  updated_at: string | null
}

export interface OutboundRouting {
  caller_id: string
  trunk_endpoint: string
  /** Nom lisible du trunk résolu (affiché dans le badge « secours »). */
  trunk_name?: string
  /** Numéro normalisé selon le format du trunk (ex: +E.164). Présent si on a
   *  passé `number` à la requête. Vide sinon. */
  dialed_number?: string
  /** true si la ligne normale de l'agent est en panne et que l'appel part par
   *  le SIP par défaut (ligne de secours). */
  fallback?: boolean
  /** Nom de la ligne en panne quand `fallback` est vrai. */
  fallback_from_name?: string
}

export interface RingTarget {
  user_id: number
  username: string
  extension: string
  last_call_at: string | null
  calls_today: number
}

export interface RingGroupTargets {
  ring_group: number
  strategy: RingStrategy
  ring_timeout_seconds?: number
  targets: RingTarget[]
  fallback: string
  reason?: string
}

export interface TransferResult {
  mode: 'blind' | 'attended'
  transferred: boolean
  to_extension: string
  to_agent: string
  bridge_id?: string
  consult_channel?: string
}

export interface CDREntry {
  id: number
  call_uuid: string
  direction: 'inbound' | 'outbound' | 'internal'
  caller_number: string
  called_number: string
  agent_username: string
  campaign: number | null
  campaign_name: string | null
  /** Disposition choisie par l'agent (nom lisible) — vide si non qualifié. */
  disposition: number | null
  disposition_title: string
  status: string
  started_at: string
  answered_at: string | null
  ended_at: string | null
  duration_seconds: number
  talk_seconds: number
  recording_uuid: string
  is_favorite?: boolean
}

export const TelephonyAPI = {
  // Asterisk system
  status: () => apiClient.get<AsteriskStatus>(`${BASE}/status/`).then((r) => r.data),
  channels: () => apiClient.get<LiveChannel[]>(`${BASE}/channels/`).then((r) => r.data),
  hangup: (channelId: string) =>
    apiClient.post(`${BASE}/channels/${channelId}/hangup/`).then((r) => r.data),
  spy: (channelId: string, mode: 'q' | 'w' | 'B') =>
    apiClient.post(`${BASE}/channels/${channelId}/spy/`, { mode }).then((r) => r.data),
  /** Transfert aveugle de SON propre appel : le serveur résout le canal de
   *  l'agent et redirige le client vers l'extension cible (mécanisme ARI). */
  transferMyCall: (toExtension: string) =>
    apiClient
      .post(`${BASE}/transfer/my-call/`, { to_extension: toExtension })
      .then((r) => r.data),
  /** Quitte l'écoute en cours (raccroche MA jambe ChanSpy — l'appel continue). */
  spyStop: (): Promise<{ stopped: number }> =>
    apiClient.post(`${BASE}/spy/stop/`).then((r) => r.data),
  originate: (endpoint: string, extension: string, callerId?: string, campaign?: number) =>
    apiClient
      .post(`${BASE}/originate/`, { endpoint, extension, caller_id: callerId, campaign })
      .then((r) => r.data),
  /** Trunk + caller-id résolus (user/équipe/global, campagne optionnelle).
   *  Si `number` est fourni, la réponse inclut `dialed_number` normalisé selon
   *  le format du trunk (ex: +E.164 pour Telnyx). */
  outboundRouting: (campaign?: number, number?: string) =>
    apiClient
      .get<OutboundRouting>(`${BASE}/outbound-routing/`, {
        params: {
          ...(campaign ? { campaign } : {}),
          ...(number ? { number } : {}),
        },
      })
      .then((r) => r.data),
  endpoints: () => apiClient.get(`${BASE}/endpoints/`).then((r) => r.data),

  // Réglages téléphonie globaux (trunk + caller-id par défaut du tenant)
  getSettings: () =>
    apiClient.get<TelephonySettings>(`${BASE}/settings/`).then((r) => r.data),
  updateSettings: (data: Partial<TelephonySettings>) =>
    apiClient.patch<TelephonySettings>(`${BASE}/settings/`, data).then((r) => r.data),

  // Trunks
  listTrunks: () => apiClient.get<Trunk[]>(`${BASE}/trunks/`).then((r) => r.data),
  createTrunk: (data: Partial<Trunk> & { password?: string }) =>
    apiClient.post<Trunk>(`${BASE}/trunks/`, data).then((r) => r.data),
  updateTrunk: (id: number, data: Partial<Trunk>) =>
    apiClient.patch<Trunk>(`${BASE}/trunks/${id}/`, data).then((r) => r.data),
  /** Re-teste la registration d'un trunk auprès de l'opérateur (sans le modifier). */
  testTrunk: (id: number) =>
    apiClient.post<Trunk>(`${BASE}/trunks/${id}/test/`).then((r) => r.data),
  deleteTrunk: (id: number) => apiClient.delete(`${BASE}/trunks/${id}/`),

  // Pool de numéros présentés d'un trunk (admin/owner). La liste peut être
  // paginée par DRF → normalisée en tableau.
  listTrunkCallerIds: (trunkId: number) =>
    apiClient
      .get<PaginatedResponse<TrunkCallerId> | TrunkCallerId[]>(`${BASE}/trunk-caller-ids/`, {
        params: { trunk: trunkId },
      })
      .then((r) => normalizePaginated<TrunkCallerId>(r.data).results),
  createTrunkCallerId: (data: Omit<TrunkCallerId, 'id'>) =>
    apiClient.post<TrunkCallerId>(`${BASE}/trunk-caller-ids/`, data).then((r) => r.data),
  updateTrunkCallerId: (id: number, data: Partial<Omit<TrunkCallerId, 'id' | 'trunk'>>) =>
    apiClient.patch<TrunkCallerId>(`${BASE}/trunk-caller-ids/${id}/`, data).then((r) => r.data),
  deleteTrunkCallerId: (id: number) => apiClient.delete(`${BASE}/trunk-caller-ids/${id}/`),

  // DIDs
  listDIDs: () => apiClient.get<DID[]>(`${BASE}/dids/`).then((r) => r.data),
  createDID: (data: Partial<DID>) => apiClient.post<DID>(`${BASE}/dids/`, data).then((r) => r.data),
  updateDID: (id: number, data: Partial<DID>) =>
    apiClient.patch<DID>(`${BASE}/dids/${id}/`, data).then((r) => r.data),
  deleteDID: (id: number) => apiClient.delete(`${BASE}/dids/${id}/`),

  // Routes
  listRoutes: () => apiClient.get<InboundRoute[]>(`${BASE}/routes/`).then((r) => r.data),
  createRoute: (data: Partial<InboundRoute>) =>
    apiClient.post<InboundRoute>(`${BASE}/routes/`, data).then((r) => r.data),
  updateRoute: (id: number, data: Partial<InboundRoute>) =>
    apiClient.patch<InboundRoute>(`${BASE}/routes/${id}/`, data).then((r) => r.data),
  deleteRoute: (id: number) => apiClient.delete(`${BASE}/routes/${id}/`),

  // Ring Groups (ACD — distribution entrante par équipe)
  listRingGroups: () => apiClient.get<RingGroup[]>(`${BASE}/ring-groups/`).then((r) => r.data),
  createRingGroup: (data: Partial<RingGroup>) =>
    apiClient.post<RingGroup>(`${BASE}/ring-groups/`, data).then((r) => r.data),
  updateRingGroup: (id: number, data: Partial<RingGroup>) =>
    apiClient.patch<RingGroup>(`${BASE}/ring-groups/${id}/`, data).then((r) => r.data),
  deleteRingGroup: (id: number) => apiClient.delete(`${BASE}/ring-groups/${id}/`),
  ringGroupTargets: (id: number) =>
    apiClient.get<RingGroupTargets>(`${BASE}/ring-groups/${id}/targets/`).then((r) => r.data),

  // Transfert d'appels entre agents
  blindTransfer: (channelId: string, toExtension: string) =>
    apiClient
      .post<TransferResult>(`${BASE}/channels/${channelId}/transfer/`, { to_extension: toExtension })
      .then((r) => r.data),
  attendedTransfer: (data: {
    customer_channel_id: string
    agent_channel_id: string
    to_extension: string
  }) =>
    apiClient.post<TransferResult>(`${BASE}/transfer/attended/`, data).then((r) => r.data),

  // CDR
  listCDR: (params: Record<string, string | number> = {}) =>
    apiClient
      .get<PaginatedResponse<CDREntry> | CDREntry[]>(`${BASE}/cdr/`, { params })
      .then((r) => normalizePaginated<CDREntry>(r.data)),
  // Mêmes filtres que listCDR — les KPIs suivent la liste affichée.
  cdrStats: (params: Record<string, string | number> = {}) =>
    apiClient.get(`${BASE}/cdr/stats/`, { params }).then((r) => r.data),
  // Supprime un appel de l'historique. 403 si l'appelant n'est pas owner/admin
  // (imposé aussi côté backend) ; 404 si le CDR n'existe pas / autre tenant.
  deleteCDR: (id: number) => apiClient.delete(`${BASE}/cdr/${id}/`),
}

// ── Pagination helpers ────────────────────────────────────────────────────
export interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

export interface PaginatedPage<T> {
  results: T[]
  count: number
  hasNext: boolean
  hasPrev: boolean
}

function normalizePaginated<T>(
  payload: PaginatedResponse<T> | T[],
): PaginatedPage<T> {
  if (Array.isArray(payload)) {
    return { results: payload, count: payload.length, hasNext: false, hasPrev: false }
  }
  return {
    results: payload.results,
    count: payload.count,
    hasNext: !!payload.next,
    hasPrev: !!payload.previous,
  }
}
